// src/components/MainAppLayout.js

import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from 'react-router-dom';

import { useWallet } from '../context/WalletContext';
import { AuthProvider } from '../contexts/AuthContext';

import VerticalNavbar from './VerticalNavbar';
import RedirectKeepingSearch from './RedirectKeepingSearch';
import MobileTabBar, { isImmersiveRoute } from './MobileTabBar';

import KolabLanding from '../pages/LandingPage';
import AccessGatePage from '../pages/AccessGatePage';

import { REELS_ENABLED } from '../config/flags';
import { creatorService } from '../services/CreatorService';
import { checkAccessCode } from '../services/accessCodeRpc';
import { isDemoMode } from '../lib/supabase';

// ── Route code splitting ─────────────────────────────────────────────────
// Every page below used to be a static import, so main.js carried all of
// them. The landing domain never renders any of them — this component
// returns early with <KolabLanding /> — yet a landing visitor still
// downloaded the admin console, the creator studio and livekit-client.
//
// KolabLanding and AccessGatePage stay eager on purpose: they are the two
// things a first-time visitor actually paints, so splitting them would add
// a round trip to first paint rather than remove one. CreatorCallPanel is
// split too — it is mounted globally, but it pulls livekit-client, and it
// only renders when a call arrives.
const DiscoverPage = lazy(() => import('../pages/DiscoverPage'));
const KolDetailPage = lazy(() => import('../pages/KOLDetailPage'));
const ChatPage = lazy(() => import('../pages/ChatPage'));
const CreatorInboxPage = lazy(() => import('../pages/CreatorInboxPage'));
const RewardsPage = lazy(() => import('../pages/RewardsPage'));
const BalancePage = lazy(() => import('../pages/BalancePage'));
const RoomPage = lazy(() => import('../pages/RoomPage'));
const ReelPage = lazy(() => import('../pages/ReelPage'));
const CreatorLivePage = lazy(() => import('../pages/CreatorLivePage'));
const CreatorDashboard = lazy(() => import('../pages/CreatorDashboard'));
const CreatorLiveStudio = lazy(() => import('../pages/CreatorLiveStudio'));
const CreateModelPage = lazy(() => import('../pages/CreateModelPage'));
const AdminLayout = lazy(() => import('../pages/AdminLayout'));
const AdminCodesPage = lazy(() => import('../pages/AdminCodesPage'));
const AdminApplicationsPage = lazy(() => import('../pages/AdminApplicationsPage'));
const AdminDropsPage = lazy(() => import('../pages/AdminDropsPage'));
const AdminPersonasPage = lazy(() => import('../pages/AdminPersonasPage'));
const AdminMediaReviewPage = lazy(() => import('../pages/AdminMediaReviewPage'));
const AdminLibraryReviewPage = lazy(() => import('../pages/AdminLibraryReviewPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const UserProfilePage = lazy(() => import('../pages/UserProfilePage'));
const DocsPage = lazy(() => import('../pages/DocsPage'));
const CreatePage = lazy(() => import('../pages/CreatePage'));
const AnswerCallPage = lazy(() => import('../pages/AnswerCallPage'));
const CamPage = lazy(() => import('../pages/CamPage'));
const CreatorCallPanel = lazy(() => import('./CreatorCallPanel'));

const ACCESS_CODE_KEY = 'lunexx_access_granted';
const STORED_CODE_KEY = 'lunexx_access_code';
// sessionStorage: the code this tab already re-validated (039-01).
const RECHECKED_CODE_KEY = 'lunexx_access_rechecked';

// 039-01: a stored grant is only as good as its code. Once per tab session
// the stored code goes back through validate_access_code:
//   valid                     → keep the grant (and remember it for the tab)
//   no stored code / invalid  → drop the grant; the gate renders
//   the rpc itself failed     → keep the grant (fail open on transport only,
//                               never on an explicit answer) and retry on
//                               the next load
const clearStoredGrant = () => {
  localStorage.removeItem(ACCESS_CODE_KEY);
  localStorage.removeItem(STORED_CODE_KEY);
  sessionStorage.removeItem(RECHECKED_CODE_KEY);
};

export const recheckStoredGrant = async () => {
  const code = localStorage.getItem(STORED_CODE_KEY);

  if (!code) {
    clearStoredGrant();
    return false;
  }

  if (
    isDemoMode ||
    sessionStorage.getItem(RECHECKED_CODE_KEY) === code
  ) {
    return true;
  }

  const result = await checkAccessCode(code);

  if (result.transportError) {
    console.warn(
      '[MainAppLayout] access re-check unavailable; keeping the stored grant',
      result.transportError,
    );
    return true;
  }

  if (!result.valid) {
    clearStoredGrant();
    return false;
  }

  sessionStorage.setItem(RECHECKED_CODE_KEY, code);
  return true;
};

// Check if we're on the landing site (lunexx.ai)
// or the app site (app.lunexx.ai).
const isLandingSite = () => {
  const hostname = window.location.hostname;

  // In development, use ?mode=landing to show the landing site.
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  ) {
    const params = new URLSearchParams(
      window.location.search,
    );

    return params.get('mode') === 'landing';
  }

  // In production, app.lunexx.ai is the application.
  return !hostname.startsWith('app.');
};

// ============================================
// DASHBOARD HOME
// ============================================

// Route chunks are fetched on demand (see the import block above), so a
// route the user has not visited yet has a load to do before it can render.
// This is the same spinner the shell already shows while it resolves wallet
// and access state, so a cold route looks like the app thinking rather than
// like the app breaking.
const RouteFallback = () => (
  <div className="flex h-full min-h-[60vh] items-center justify-center bg-[#0F0F0F]">
    <div className="relative h-12 w-12">
      <div className="absolute inset-0 rounded-full border-2 border-[#A3E635]/20" />

      <div className="absolute inset-0 animate-spin rounded-full border-2 border-[#A3E635] border-t-transparent" />
    </div>
  </div>
);

const HomePage = () => (
  <div className="custom-scrollbar h-full overflow-y-auto bg-[#0F0F0F] p-8 text-white">
    <div className="mx-auto max-w-6xl space-y-12">
      <header className="space-y-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">
          Dashboard
        </span>

        <h1 className="text-[32px] font-bold uppercase italic tracking-tighter">
          Welcome back
        </h1>
      </header>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#1E1E1E] p-8">
          <div className="absolute right-0 top-0 h-full w-1.5 bg-[#A3E635]" />

          <h3 className="mb-2 text-[20px] font-bold tracking-tight">
            Deploy Agent
          </h3>

          <p className="mb-8 max-w-[240px] text-[14px] leading-relaxed text-zinc-500">
            Scale your social marketing with autonomous AI
            nodes.
          </p>

          <button
            type="button"
            className="rounded-xl bg-white px-6 py-3 text-[13px] font-bold text-black transition-all hover:bg-[#F2F2F2] active:scale-95"
          >
            Launch Setup
          </button>
        </div>

        <div className="group rounded-[32px] border border-white/5 bg-[#1E1E1E] p-8 transition-all hover:border-white/10">
          <h3 className="mb-2 text-[20px] font-bold tracking-tight text-zinc-300 transition-colors group-hover:text-white">
            Active Chats
          </h3>

          <p className="mb-8 text-[14px] leading-relaxed text-zinc-500">
            Review conversations and respond to your
            community.
          </p>

          <Link
            to="/studio/inbox"
            className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 px-6 py-3 text-[13px] font-bold text-white transition-all hover:bg-white/10"
          >
            Open Inbox
          </Link>
        </div>
      </div>
    </div>
  </div>
);

// Wrapper that provides the existing AuthContext used by CreateModelPage.
const CreateModelPageWithAuth = () => (
  <AuthProvider>
    <CreateModelPage />
  </AuthProvider>
);

// ============================================
// MAIN APP LAYOUT
// ============================================

const MainAppLayout = () => {
  const {
    isLoading: walletLoading,
    walletAddress,
    currentUser,
  } = useWallet();

  const [
    isNavbarCollapsed,
    setIsNavbarCollapsed,
  ] = useState(false);

  const [
    hasAccessCode,
    setHasAccessCode,
  ] = useState(false);

  const [
    checkingAccess,
    setCheckingAccess,
  ] = useState(true);

  const [
    isLanding,
    setIsLanding,
  ] = useState(false);

  const [
    creatorRecord,
    setCreatorRecord,
  ] = useState(null);

  const [
    creatorLoading,
    setCreatorLoading,
  ] = useState(true);

  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const landing = isLandingSite();

    setIsLanding(landing);

    const accessGranted =
      localStorage.getItem(
        ACCESS_CODE_KEY,
      ) === 'true';

    // The landing domain never renders the app, so it never re-checks.
    if (landing || !accessGranted) {
      setHasAccessCode(accessGranted);
      setCheckingAccess(false);
      return undefined;
    }

    // The spinner stays up until the stored grant is re-validated, so
    // AccessGatePage never mounts with keys this check is about to clear.
    recheckStoredGrant().then((granted) => {
      if (cancelled) return;
      setHasAccessCode(granted);
      setCheckingAccess(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCreator = async () => {
      if (!walletAddress) {
        if (!cancelled) {
          setCreatorRecord(null);
          setCreatorLoading(false);
        }

        return;
      }

      setCreatorLoading(true);

      try {
        const result =
          await creatorService.getByWalletAddress(
            walletAddress,
          );

        // Supports either:
        // creator
        // or { data: creator, error }
        const creator =
          result?.data ??
          result ??
          null;

        if (cancelled) {
          return;
        }

        setCreatorRecord(creator);

        const resolvedCreatorId =
          creator?.creator_id ??
          creator?.id ??
          null;

        const resolvedStatus =
          typeof creator?.status === 'string'
            ? creator.status.toLowerCase()
            : null;

        const resolvedIsApprovedCreator =
          Boolean(resolvedCreatorId) &&
          (
            resolvedStatus === 'approved' ||
            resolvedStatus === null
          );

        console.log(
          '[MainAppLayout] creator resolution',
          {
            walletAddress,
            creatorId: resolvedCreatorId,
            status: resolvedStatus,
            handle:
              creator?.handle ??
              null,
            creatorType:
              creator?.creator_type ??
              null,
            isApprovedCreator:
              resolvedIsApprovedCreator,
          },
        );
      } catch (error) {
        console.error(
          '[MainAppLayout] creator lookup failed:',
          error,
        );

        if (!cancelled) {
          setCreatorRecord(null);
        }
      } finally {
        if (!cancelled) {
          setCreatorLoading(false);
        }
      }
    };

    loadCreator();

    return () => {
      cancelled = true;
    };
  }, [walletAddress]);

  const creatorId =
    creatorRecord?.creator_id ??
    creatorRecord?.id ??
    null;

  const creatorStatus =
    typeof creatorRecord?.status === 'string'
      ? creatorRecord.status.toLowerCase()
      : null;

  const isApprovedCreator =
    Boolean(creatorId) &&
    (
      creatorStatus === 'approved' ||
      creatorStatus === null
    );

  // Existing creator-specific inbox and call behavior.
  const isCreator =
    isApprovedCreator;

  const handleNavbarToggle = (
    collapsed,
  ) => {
    setIsNavbarCollapsed(collapsed);
  };

  // Show loading while checking wallet/access state.
  if (
    walletLoading ||
    checkingAccess
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0F0F0F]">
        <div className="relative h-12 w-12">
          <div className="absolute inset-0 rounded-full border-2 border-[#A3E635]/20" />

          <div className="absolute inset-0 animate-spin rounded-full border-2 border-[#A3E635] border-t-transparent" />
        </div>
      </div>
    );
  }

  // Landing domain: render only the public landing site.
  if (isLanding) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <KolabLanding
              showAccessGate={false}
            />
          }
        />
      </Routes>
    );
  }

  // Application domain without access approval.
  if (!hasAccessCode) {
    return (
      <Routes>
        <Route
          path="*"
          element={<AccessGatePage />}
        />
      </Routes>
    );
  }

  const getCurrentPageFromPath = (
    pathname,
  ) => {
    const path =
      pathname.split('/')[1] ||
      'discover';

    if (
      path === 'kol' ||
      path === 'creator' ||
      path === 'room' ||
      path === 'u'
    ) {
      return 'discover';
    }

    return path;
  };

  const shouldMountCreatorCallPanel =
    !creatorLoading &&
    isCreator &&
    location.pathname !== '/answer-call';

  // The bottom bar is suppressed on routes that own the whole screen, and
  // <main> only reserves space for it when it is actually there — otherwise
  // an immersive route gets 56px of dead padding under a full-bleed player.
  const showMobileNav = !isImmersiveRoute(location.pathname);

  return (
    /* h-[100dvh], not h-screen: on mobile browsers 100vh is the viewport
       WITHOUT the address bar, so h-screen overflows by exactly the height
       of the browser chrome and pushes the bottom tab bar off-screen.
       md:h-screen keeps desktop on the older unit where dvh buys nothing.

       `relative` is load-bearing: it makes this box the containing block
       of the ambient glow below. <main> is not positioned, so without it
       the glow (absolute, -right-[120px], 740px wide) resolved against the
       initial containing block, which no overflow clips — it made the
       DOCUMENT 120px wider than the phone on every route, and a finger
       could drag the whole app sideways onto bare background. With this
       box as its containing block, this box's overflow-hidden clips it. */
    <div className="relative flex h-[100dvh] md:h-screen overflow-hidden bg-[#0F0F0F] font-sans">
      <VerticalNavbar
        currentPage={getCurrentPageFromPath(
          location.pathname,
        )}
        currentUser={currentUser}
        creatorProfile={creatorRecord}
        onToggle={handleNavbarToggle}
        isApprovedCreator={
          !creatorLoading &&
          isApprovedCreator
        }
      />

      {/* The rail's margin is DESKTOP ONLY. Unprefixed ml-14/ml-56 was the
          single biggest mobile blocker: at 390px it surrendered 14-56% of
          the screen to a nav that is not even rendered there. Under md the
          margin is zero and the tab bar takes the bottom instead. */}
      <main
        /* overscroll-x-none, NOT overflow-x-hidden.

           <main> is overflow-auto on BOTH axes, so on touch it rubber-bands
           sideways even though scrollWidth === clientWidth and there is
           nothing to scroll to. That bounce is what moved the page left and
           right on Discover.

           overscroll-behavior only disables the bounce; it leaves real
           horizontal scrolling intact, so a page that genuinely overflows
           still shows it as a bug instead of having it silently clipped.
           overflow-x-hidden would have hidden that AND risked killing
           position: sticky for descendants whose scroll container is this
           element — DiscoverPage's sticky tab header is one. */
        className={`flex-1 overflow-y-auto overflow-x-hidden overscroll-x-none transition-all duration-300 ml-0 ${isNavbarCollapsed
            ? 'md:ml-14'
            : 'md:ml-56'
          } ${showMobileNav ? 'pb-[calc(var(--lunexx-tabbar-h)+env(safe-area-inset-bottom))] md:pb-0' : ''}`}
      >
        {/* Ambient glow.

            Was a 500px circle under filter: blur(120px). That is 250,000
            pixels of the most expensive filter on the page, on EVERY route,
            and a 120px kernel is what makes mobile compositors drop and
            re-rasterise the layer mid-scroll — which reads to the user as
            the artwork vanishing and slowly coming back.

            A radial-gradient is the same soft falloff with no filter at
            all. Box is grown 740px and offset -120px so the light lands
            where the blurred circle's spread used to reach. */}
        <div className="pointer-events-none absolute -right-[120px] -top-[120px] z-0 h-[740px] w-[740px] rounded-full bg-[radial-gradient(circle_closest-side,rgba(163,230,53,0.055)_0%,rgba(163,230,53,0.03)_38%,rgba(163,230,53,0.008)_62%,transparent_78%)]" />

        <div className="relative z-10 h-full w-full">
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Default */}
            <Route
              path="/"
              element={
                <Navigate
                  to="/discover"
                  replace
                />
              }
            />

            {/* Main pages */}
            <Route
              path="/home"
              element={<HomePage />}
            />

            <Route
              path="/discover"
              element={<DiscoverPage />}
            />

            <Route
              path="/create"
              element={<CreatePage />}
            />

            {/* My Agent became Create (021-01); old deep links keep their ?tab= */}
            <Route
              path="/my-agents"
              element={<RedirectKeepingSearch to="/create" />}
            />

            <Route
              path="/chats"
              element={
                creatorLoading ? (
                  <div className="flex h-full items-center justify-center bg-[#0F0F0F]">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/15 border-t-white" />
                  </div>
                ) : isCreator ? (
                  <CreatorInboxPage
                    creatorWallet={
                      walletAddress
                    }
                  />
                ) : (
                  <ChatPage />
                )
              }
            />

            <Route
              path="/studio/inbox"
              element={
                <Navigate
                  to="/chats"
                  replace
                />
              }
            />

            <Route
              path="/rewards"
              element={<RewardsPage />}
            />

            <Route
              path="/profile"
              element={<ProfilePage />}
            />

            <Route
              path="/balance"
              element={<BalancePage />}
            />

            <Route
              path="/answer-call"
              element={<AnswerCallPage />}
            />

            <Route
              path="/cam"
              element={<CamPage />}
            />

            {/* Creator Studio */}
            <Route
              path="/studio"
              element={<CreatorDashboard />}
            />

            <Route
              path="/studio/live/:roomId"
              element={<CreatorLiveStudio />}
            />

            {/* Admin — one hub, tabbed. Nested routes keep every old deep
                link (/admin/personas, /admin/drops, …) working unchanged. */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/personas" replace />} />
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route path="personas" element={<AdminPersonasPage />} />
              <Route path="drops" element={<AdminDropsPage />} />
              <Route path="media-review" element={<AdminMediaReviewPage />} />
              <Route path="library" element={<AdminLibraryReviewPage />} />
              <Route path="codes" element={<AdminCodesPage />} />
            </Route>

            {/* Creator application */}
            <Route
              path="/create-model"
              element={
                <CreateModelPageWithAuth />
              }
            />

            {/* Public user profile */}
            <Route
              path="/u/:username"
              element={<UserProfilePage />}
            />

            {/* Documentation (white-background reader) */}
            <Route
              path="/docs"
              element={<DocsPage />}
            />

            {/* Creator pages */}
            <Route
              path="/kol/:name"
              element={<KolDetailPage />}
            />

            <Route
              path="/creator/:handle"
              element={<KolDetailPage />}
            />

            <Route
              path="/creator/:handle/live"
              element={<CreatorLivePage />}
            />

            {/* Rooms */}
            <Route
              path="/room/:roomId"
              element={<RoomPage />}
            />

            {/* Shareable single reel */}
            {REELS_ENABLED && (
              <Route
                path="/reel/:reelId"
                element={<ReelPage />}
              />
            )}

            {/* Catch-all */}
            <Route
              path="*"
              element={
                <Navigate
                  to="/discover"
                  replace
                />
              }
            />
          </Routes>
          </Suspense>
        </div>
      </main>

      {/* Global creator call receiver — mount exactly once */}
      {shouldMountCreatorCallPanel && (
        <Suspense fallback={null}>
          <CreatorCallPanel />
        </Suspense>
      )}

      {/* MOBILE NAV — replaces the rail under md. Same item definitions,
          imported from VerticalNavbar rather than duplicated. */}
      {showMobileNav && (
        <MobileTabBar
          currentUser={currentUser}
          isConnected={!!currentUser}
          isApprovedCreator={!creatorLoading && isApprovedCreator}
          creatorProfile={creatorRecord}
        />
      )}

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a3e635;
        }
      `}</style>
    </div>
  );
};

export default MainAppLayout;